import { ListItemIcon, ListItemText, Skeleton, Stack } from '@mui/material';
import { type LinkProps } from '@tanstack/react-router';
import { CheckIcon } from 'lucide-react';
import { memo, Suspense, useMemo, type PropsWithChildren } from 'react';
import { hasPermissionsWithoutRpc } from '@/api/permissions';
import { ChipPill } from '@/components/Common/SeverityPill';
import { Icon } from '@/components/Icon/Icon';
import { useEffectivePermissionsForAccountId } from '@/hooks/useEffectivePermissionsForAccountId';
import { ListItemButtonLink } from '@/router/Link';
import { useSortedUserAccounts } from '@/routes/_authenticated.hooks';
import {
  useSafeAccountId,
  useSelectedAccountId,
} from '@/routes/_authenticated/_dashboard/accounts/$accountId/route.hooks';

const AccountListItemsSkeleton = () => {
  const accounts = useSortedUserAccounts();

  const maxCharacterLength = useMemo(
    () => accounts.reduce((max, account) => Math.max(max, account.name.length), 0),
    [accounts],
  );

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      {accounts.map((account) => (
        <Skeleton
          key={account.id}
          variant="rounded"
          width={`${maxCharacterLength}ch`}
          height={40}
          sx={{ bgcolor: 'action.hover' }}
        />
      ))}
    </Stack>
  );
};

export const AccountNavigationItems = memo(function AccountNavigationItems({
  ownerEmail,
  onItemNavigate,
}: {
  ownerEmail: string;
  onItemNavigate: () => void;
}) {
  const selectedAccountId = useSelectedAccountId();
  const isAccountIdPresent = Boolean(useSafeAccountId());

  return (
    <Suspense fallback={<ListItemText primary={<AccountListItemsSkeleton />} />}>
      {useSortedUserAccounts().map((account) => (
        <AccountNavigationItem
          key={account.id}
          isOwner={account.ownerEmail.toLocaleLowerCase() === ownerEmail.toLocaleLowerCase()}
          accountId={account.id}
          selectedAccountId={selectedAccountId}
          onClick={onItemNavigate}
          deepLinking={isAccountIdPresent}
        >
          {account.name}
        </AccountNavigationItem>
      ))}
    </Suspense>
  );
});

const AccountNavigationItem = ({
  children,
  isOwner,
  accountId,
  deepLinking,
  selectedAccountId,
  onClick,
}: PropsWithChildren<{
  isOwner: boolean;
  accountId: string;
  deepLinking: boolean;
  selectedAccountId?: string;
  onClick: () => void;
}>) => {
  const isActive = selectedAccountId === accountId;
  const permissions = useEffectivePermissionsForAccountId(accountId);
  // `read:profile` is one of the permissions no RPC declares, so `hasPermissionsWithoutRpc` is the
  // only typed way to name it. Holding nothing but it means the account grants no real access.
  const hasOnlyReadProfileAccess = permissions.size === 1 && hasPermissionsWithoutRpc(['read:profile'], permissions);

  // - On a deep link possible account route with full access, change the accountID path param.
  // - Otherwise the user routes or accounts, we can only read the profile of carry the account in the accountID
  // search param so the selection actually takes effect. Without this, selecting an account on a user
  // route did nothing
  const canDeepLink = deepLinking && !hasOnlyReadProfileAccess;
  const to: LinkProps['to'] = deepLinking && !canDeepLink ? '/get-started' : '.';
  const linkParams = canDeepLink ? { accountId } : undefined;
  const linkSearch = canDeepLink ? undefined : { account_id: accountId };

  return (
    <ListItemButtonLink
      to={to}
      params={linkParams}
      search={linkSearch}
      selected={isActive}
      dense={true}
      sx={{ gap: 1 }}
      onClick={onClick}
    >
      <>
        <ListItemText
          data-sentry-mask={true}
          primary={
            isOwner ? (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Stack
                  component="span"
                  sx={{
                    display: 'block',
                    maxWidth: 320,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {children}
                </Stack>
                <ChipPill color="success" label="owner" sx={{ flexShrink: 0 }} />
              </Stack>
            ) : (
              <Stack
                component="span"
                sx={{
                  display: 'block',
                  maxWidth: 320,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {children}
              </Stack>
            )
          }
        />
        {isActive && (
          <ListItemIcon sx={{ mx: 'auto' }}>
            <Icon icon={CheckIcon} size={20} sx={{ color: 'primary.main' }} />
          </ListItemIcon>
        )}
      </>
    </ListItemButtonLink>
  );
};
